import React from 'react';
import { Card, CardTitle } from '@raptormaps/card';
import { Text } from '@raptormaps/text';
import { Org } from '../../api/services/orgs/orgs.schema';
import { formatDate } from '../../utils/general_utils';
import {
  IdentificationGrid,
  IdentificationColumn,
  IdentificationField,
  FieldLabel,
  FieldValue,
  EmDash,
  ContactBlock,
  LoadingContainer,
} from './OrgDetails.styles';

interface OrgIdentificationCardProps {
  org: Org;
  isLoading: boolean;
}

function NullableValue({
  value,
}: {
  value: string | number | null | undefined;
}) {
  if (value === null || value === undefined) return <EmDash>—</EmDash>;
  return <FieldValue>{String(value)}</FieldValue>;
}

export function OrgIdentificationCard({
  org,
  isLoading,
}: OrgIdentificationCardProps) {
  if (isLoading) {
    return (
      <Card>
        <CardTitle>Identification & Metadata</CardTitle>
        <LoadingContainer>
          <Text variant="paragraph_medium" color="#718096">
            Loading...
          </Text>
        </LoadingContainer>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>Identification & Metadata</CardTitle>
      <IdentificationGrid>
        {/* Column 1: Internal Name, Org ID */}
        <IdentificationColumn>
          <IdentificationField>
            <FieldLabel>Internal Name</FieldLabel>
            <NullableValue value={org.name} />
          </IdentificationField>

          <IdentificationField>
            <FieldLabel>Org ID</FieldLabel>
            <NullableValue value={org.id} />
          </IdentificationField>

          <IdentificationField>
            <FieldLabel>Is Manufacturer</FieldLabel>
            <NullableValue
              value={
                org.is_manufacturer === null ||
                org.is_manufacturer === undefined
                  ? null
                  : org.is_manufacturer
                  ? 'Yes'
                  : 'No'
              }
            />
          </IdentificationField>
        </IdentificationColumn>

        {/* Column 2: Org Type, Tier Class, Created At */}
        <IdentificationColumn>
          <IdentificationField>
            <FieldLabel>Org Type</FieldLabel>
            <NullableValue value={org.type} />
          </IdentificationField>

          <IdentificationField>
            <FieldLabel>Tier Class</FieldLabel>
            <NullableValue value={org.class_name} />
          </IdentificationField>

          <IdentificationField>
            <FieldLabel>Created At</FieldLabel>
            <FieldValue>{formatDate(org.created_tsecs)}</FieldValue>
          </IdentificationField>
        </IdentificationColumn>

        {/* Column 3: Primary Contact, Warranty Claim Email */}
        <IdentificationColumn>
          <IdentificationField>
            <FieldLabel>Primary Contact</FieldLabel>
            <ContactBlock>
              <NullableValue value={org.api_phone} />
            </ContactBlock>
          </IdentificationField>

          <IdentificationField>
            <FieldLabel>Warranty Claim Email</FieldLabel>
            <NullableValue value={org.warranty_claim_email} />
          </IdentificationField>
        </IdentificationColumn>
      </IdentificationGrid>
    </Card>
  );
}
